import React, { FC, useState } from "react";
import { DatePicker, Dropdown, MenuProps } from "antd";
import { DownOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

interface AnalyticsDatePickerProps {
  selectedDate: any;
  setSelectedDate: (date: any) => void;
  selectedDateFormatted: string;
  setSelectedDateFormatted: (formatted: string) => void;
  type: string;
  setType: (type: string) => void;
  onDateChange: (startDate: string, endDate: string) => void;
  disabled?: boolean;
}

const AnalyticsDatePicker: FC<AnalyticsDatePickerProps> = ({
  selectedDate,
  setSelectedDate,
  selectedDateFormatted,
  setSelectedDateFormatted,
  type,
  setType,
  onDateChange,
  disabled = false,
}) => {
  const [visible, setVisible] = useState(false);
  const typeCapitalize = type.charAt(0).toUpperCase() + type.slice(1);

  const handleDayDateChange = (date: any, dateString: any) => {
    if (date) {
      setType("day");
      setVisible(false);
      setSelectedDateFormatted(dateString);
      setSelectedDate(date);
      const start = dayjs(date.toDate()).startOf("day");
      const end = dayjs(date.toDate()).endOf("day");
      onDateChange(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    }
  };

  const handleWeekDateChange = (date: any, dateString: any) => {
    if (date) {
      setSelectedDateFormatted(dateString);
      setSelectedDate(date);
      setType("week");
      setVisible(false);

      const start = dayjs(date.toDate()).startOf("week");
      const end = dayjs(date.toDate()).endOf("week");
      onDateChange(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    }
  };

  const handleMonthDateChange = (date: any, dateString: any) => {
    if (date) {
      setSelectedDateFormatted(dateString);
      setSelectedDate(date);
      setType("month");
      setVisible(false);

      const start = dayjs(date.toDate()).startOf("month");
      const end = dayjs(date.toDate()).endOf("month");
      onDateChange(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    }
  };

  const handleYearDateChange = (date: any, dateString: any) => {
    if (date) {
      setSelectedDateFormatted(dateString);
      setSelectedDate(date);
      setType("year");
      setVisible(false);

      const start = dayjs(date.toDate()).startOf("year");
      const end = dayjs(date.toDate()).endOf("year");
      onDateChange(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    }
  };

  const handleDateRangeChange = (dates: any, dateStrings: any) => {
    if (
      dates &&
      dates[0] &&
      dates[1] &&
      (selectedDate[0] !== dates[0] || selectedDate[1] !== dates[1])
    ) {
      setSelectedDate(dates);
      setSelectedDateFormatted(dateStrings.join(" to "));
      setType("Custom");
      setVisible(false);

      const start = dayjs(dates[0].toDate()).startOf("day");
      const end = dayjs(dates[1].toDate()).endOf("day");
      onDateChange(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    }
  };

  const items: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <h5 style={{ minWidth: "80px", margin: 0 }}>Daily:</h5>
          <div style={{ flex: 1 }} onClick={(e) => e.stopPropagation()}>
            <DatePicker
              style={{ width: "100%" }}
              value={type === "day" ? selectedDate : null}
              onChange={handleDayDateChange}
              disabledDate={(current) => current > dayjs().endOf("day")}
            />
          </div>
        </div>
      ),
    },
    {
      key: "2",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <h5 style={{ minWidth: "80px", margin: 0 }}>Weekly:</h5>
          <div style={{ flex: 1 }} onClick={(e) => e.stopPropagation()}>
            <DatePicker
              style={{ width: "100%" }}
              picker="week"
              value={type === "week" ? selectedDate : null}
              onChange={handleWeekDateChange}
              disabledDate={(current) => current > dayjs().endOf("week")}
            />
          </div>
        </div>
      ),
    },
    {
      key: "3",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <h5 style={{ minWidth: "80px", margin: 0 }}>Monthly:</h5>
          <div style={{ flex: 1 }} onClick={(e) => e.stopPropagation()}>
            <DatePicker
              style={{ width: "100%" }}
              picker="month"
              value={type === "month" ? selectedDate : null}
              onChange={handleMonthDateChange}
              disabledDate={(current) => current > dayjs().endOf("month")}
            />
          </div>
        </div>
      ),
    },
    {
      key: "4",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <h5 style={{ minWidth: "80px", margin: 0 }}>Yearly:</h5>
          <div style={{ flex: 1 }} onClick={(e) => e.stopPropagation()}>
            <DatePicker
              style={{ width: "100%" }}
              picker="year"
              value={type === "year" ? selectedDate : null}
              onChange={handleYearDateChange}
              disabledDate={(current) => current > dayjs().endOf("year")}
            />
          </div>
        </div>
      ),
    },
    {
      key: "5",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <h5 style={{ minWidth: "80px", margin: 0 }}>Custom:</h5>
          <div style={{ flex: 1 }} onClick={(e) => e.stopPropagation()}>
            <RangePicker
              style={{ width: "100%" }}
              value={type === "Custom" ? selectedDate : null}
              onCalendarChange={handleDateRangeChange}
              disabledDate={(current) => current > dayjs().endOf("day")}
            />
          </div>
        </div>
      ),
    },
  ];

  return (
    <Dropdown
      menu={{ items, selectable: true }}
      trigger={["click"]}
      placement="bottomLeft"
      open={visible}
      disabled={disabled}
      onOpenChange={(v) => setVisible(v)}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          height: "37px",
          backgroundColor: !disabled ? "#fff" : "#e9ecef",
          color: "#5e6278",
          border: "1px solid #1976d2",
          borderRadius: "4px",
          fontWeight: 500,
          fontSize: "0.9rem",
          padding: "0.5rem 1rem",
          cursor: disabled ? "not-allowed" : "pointer",
          minWidth: "200px",
        }}
      >
        {typeCapitalize}: {selectedDateFormatted}
        <DownOutlined style={{ marginLeft: "auto" }} />
      </div>
    </Dropdown>
  );
};

export default AnalyticsDatePicker;
